import { FETCH_STATUS } from "descriptions";
import { makeAutoObservable, runInAction } from "mobx";

interface IHeaders {
	[key: string]: any;
}

export interface IFetchDataReturn<T = any, M = any> {
	status: FETCH_STATUS;
	data?: T;
	meta?: M;
	message: string;
}

export interface IFetchServiceParams {
	url: string;
	method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
	headers?: IHeaders;
	body?: any;
	isFormData?: boolean;
	formatData?: (data: any) => any;
}
export interface IFetchService<T = any, M = any> {
	loading: FETCH_STATUS;
	error?: string;
	data?: T;
	meta?: M;
	message: string;

	fetchData: (params: IFetchServiceParams) => Promise<IFetchDataReturn<T, M>>;
	setData: (newData: any) => void;
}

class FetchService<T = any, M = any> implements IFetchService {
	loading: FETCH_STATUS = FETCH_STATUS.WAIT;
	error?: string;
	data?: T;
	meta?: M;
	message: string = "";

	constructor() {
		makeAutoObservable(this);
	}

	setData = (newData: any) => {
		this.data = newData;
	};

	fetchData = async (params: IFetchServiceParams): Promise<IFetchDataReturn<T, M>> => {
		const {
			url,
			method = "GET",
			headers = { "Content-Type": "application/json", Accept: "application/json" },
			body,
			isFormData,
			formatData,
		} = params;
		this.loading = FETCH_STATUS.PENDING;
		this.error = undefined;
		this.message = "";
		try {
			const options: any = { method };

			if (!isFormData) {
				options.headers = { ...options.headers, ...headers };
			}
			if (body) {
				options.body = !isFormData ? JSON.stringify(body) : body;
			}
			const response = await fetch(url, { ...options });
			const json = await response.json();
			return runInAction(() => {
				const message = Array.isArray(json.message || json.messages)
					? (json.message || json.messages).join(" ")
					: json?.message || "";
				this.message = message;
				if (response.ok) {
					this.loading = FETCH_STATUS.DONE;
					this.data = !!formatData ? formatData(json.data) : json.data;
					this.meta = json?.meta;

					return {
						status: FETCH_STATUS.DONE,
						data: !!formatData ? formatData(json.data) : json.data,
						meta: json?.meta,
						message,
					};
				} else {
					this.loading = FETCH_STATUS.FAILED;
					this.error = json?.message || "";

					return {
						status: FETCH_STATUS.FAILED,
						message,
					};
				}
			});
		} catch (e) {
			runInAction(() => {
				this.loading = FETCH_STATUS.FAILED;
			});
			return {
				status: FETCH_STATUS.FAILED,
				message: "Unknown error from server",
			};
		}
	};
}

export default FetchService;
